import { useCallback } from 'react';
import type { VBandejaActividad } from '@aosenuma/lib-types';
import { getSupabase, isSupabaseConfigured } from '../client';
import { fetchAllPaginated } from '../paginate';
import { useCachedFetch } from './useCachedFetch';

/**
 * Vista `v_bandeja_actividad` (mig 0053). Solo parcelas con gestion existente
 * (= con actividad real), ordenadas por `last_activity_at DESC`.
 *
 * Reemplaza el uso de `useParcelasTecnicas()` en `BandejaPage` para no
 * inundar al técnico con las 2,374 parcelas RAN sin actividad.
 *
 * Cache: stale-while-revalidate por sessionStorage (ttl 60s). Misma
 * convención que los otros hooks de inventario — el técnico que vuelve
 * al tab desde otra pestaña ve el último estado al instante mientras
 * la query revalida en background.
 */
export function useBandejaActividad(): {
  data: VBandejaActividad[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
} {
  const fetcher = useCallback(async (): Promise<VBandejaActividad[]> => {
    if (!isSupabaseConfigured()) return [];
    const sb = getSupabase();
    return fetchAllPaginated<VBandejaActividad>(
      (from, to) =>
        sb
          .from('v_bandeja_actividad')
          .select('*')
          .order('last_activity_at', { ascending: false })
          .range(from, to),
      1000,
      20_000,
    );
  }, []);

  const { data, loading, error, refetch } = useCachedFetch<VBandejaActividad[]>(
    'bandeja_actividad',
    fetcher,
    { ttlMs: 60_000 },
  );

  return { data: data ?? [], loading, error, refetch };
}
